import { Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { JugglingScreen } from "@motleyhand/design"
import { useGetSettingsQuery, useUpdateSettingsMutation } from "./api"
import { getTenantSlug, setTenantSlug, useRedirectAfterLogin } from "@/auth"
import { baseApi, clearAbly, useDispatch, useSelector } from "@/base"

type Props = {
  tenantSlug: string | undefined
}

export default function TenantRoute({ tenantSlug }: Props) {
  const location = useLocation()
  const dispatch = useDispatch()
  const { setRedirectUrl } = useRedirectAfterLogin()
  const { data: settings, isLoading: isSettingsLoading } = useGetSettingsQuery()
  const [updateSettings] = useUpdateSettingsMutation()
  const navigate = useNavigate()
  const storedTenantSlug = useSelector(getTenantSlug)

  const currentTenantSlug = settings?.currentTenantSlug

  // BC: Redirect to prefixed path
  useEffect(() => {
    if (tenantSlug || isSettingsLoading) {
      return
    }

    if (!currentTenantSlug) {
      setRedirectUrl()
      void navigate({ to: "/onboarding" })

      return
    }

    if (location.pathname.startsWith("/t/")) {
      return
    }

    void navigate({
      to: `/t/${currentTenantSlug}/` + location.pathname,
      search: location.search,
      replace: true,
    })
  }, [
    currentTenantSlug,
    setRedirectUrl,
    isSettingsLoading,
    location.pathname,
    location.search,
    navigate,
    tenantSlug,
  ])

  // Store as current when switching
  useEffect(() => {
    if (!tenantSlug || isSettingsLoading) {
      return
    }
    if (currentTenantSlug === tenantSlug) {
      return
    }

    void updateSettings({ currentTenantSlug: tenantSlug })
  }, [currentTenantSlug, isSettingsLoading, tenantSlug, updateSettings])

  // Persist in store for RTK api calls
  useEffect(() => {
    if (!tenantSlug || storedTenantSlug === tenantSlug) {
      return
    }

    if (storedTenantSlug) {
      clearAbly()
      dispatch(baseApi.util.resetApiState())
    }
    dispatch(setTenantSlug(tenantSlug))
  }, [tenantSlug, storedTenantSlug, dispatch])

  if (!storedTenantSlug || storedTenantSlug !== tenantSlug) {
    return <JugglingScreen />
  }

  return <Outlet />
}
